import React, {ReactElement} from "react";
import PropTypes from "prop-types";

import {
  PortalLogo,
  ActionButton,
  Notification
} from "./children";

import type {PayTheoryColor} from "@paytheory/components.common.global_style";

export type PortalHeadProps = {
  trailingButtons?: {
    icon: string,
    action: () => void,
    tooltip: string
  }[],
  color?: PayTheoryColor,
  primaryElement?: ReactElement
}

export const PortalHead = ({ trailingButtons, color, primaryElement }: PortalHeadProps) => {
  const buttons = trailingButtons.map((item, index) => {
      return (
          <ActionButton
              icon={item.icon}
              action={item.action}
              key={`${item.icon}-${index}`}
              tooltip={item.tooltip}
          />
      );
  });

  return (
      <React.Fragment>
        <div className="portal-notifications">
          <Notification />
        </div>
        <div className="portal-head-header" style={{
          backgroundColor: `var(--${color || "grey-3"})`,
        }}>
          <div className="portal-logo-wrapper">
            {primaryElement ? primaryElement : <PortalLogo />}
          </div>
          <div className="portal-buttons">{buttons.length ? buttons : null}</div>
          <style>{`
          .portal-head-header {
            background-size: 100%;
            background-position: bottom;
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            padding: 0px 0px 0px 24px;
            line-height: 30px;
            height: 64px;
            min-height: 64px;
            box-shadow: 1px 1px 1px var(--black-opaque-8);
            z-index: 10;
            position: -webkit-sticky;
            position: sticky;
            top: 0px;
          }

          .portal-head-header .portal-logo-wrapper {
            width: 290px;
          }

          .portal-notifications {
            display: flex;
            justify-content: center;
            flex-grow: 1;
            position: absolute;
            height: 0px;
            top: 0px;
            width: 100%;
            z-index: 500;
          }

          .portal-notifications .notification {
            display: flex;
            height: 40px;
            justify-content: space-between;
            flex-grow: 1;
            align-items: center;
            padding: 0px 8px 0px 16px;
            margin: 0px 320px;
            border-radius: 8px;
            color: var(--black);
            position: absolute;
            top: -50px;
            max-width: calc(100% - 540px);
            transition: top 0.5s ease;
            overflow: hidden;
          }

          .portal-notifications .notification p {
            margin-right: 24px;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
          }

          .portal-notifications .notification.show {
            top: 12px;
            transition: top 0.5s ease;
          }

          .portal-notifications .notification .close-message {
            cursor: pointer;
            height: 32px;
            width: 32px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 8px;
          }

          .portal-notifications .notification .close-message:hover {
            background: var(--black-opaque-8);
          }

          .portal-head-header .portal-buttons {
            display: flex;
            justify-content: flex-end;
            align-items: center;
            width: 200px;
            padding-right: 16px;
          }

          .portal-head-header .pt-portal-head-menu-button,
          .portal-head-header .pt-portal-head-action-button {
            height: 40px;
            width: 40px;
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
            border-radius: 8px;
          }

          .portal-head-header .pt-portal-head-menu-button:hover,
          .portal-head-header .pt-portal-head-action-button:hover {
            background: var(--grey-1-opaque);
          }

          .portal-head-header .pt-portal-head-action-button {
            cursor: pointer;
          }

          @media only screen and (max-width: 500px) {
            .portal-notifications .notification {
              width: 98%;
              max-width: 98%;
            }
          }
        `}</style>
        </div>
      </React.Fragment>
  );
};

PortalHead.propTypes = {
  trailingButtons: PropTypes.array,
  color: PropTypes.string,
  primaryElement: PropTypes.element
};

PortalHead.defaultProps = {
  trailingButtons: []
};

const showNotification = (color, border) => (message, stay = false) => {
  const element = document.getElementById(`pt-notification`);
  // @ts-ignore
  if (element.timeoutId) {
    // @ts-ignore
    clearTimeout(element.timeoutId);
  }
  const manageNotification = () => {
    element.style.backgroundColor = `var(--${color})`;
    element.style.borderColor = `var(--${border})`;
    element.querySelector("p").innerHTML = message;
    element.classList.add("show");
    // @ts-ignore
    element.timeoutId = setTimeout(() => {
      element.classList.remove("show");
      // @ts-ignore
      if (element.timeoutId) {
        // @ts-ignore
        element.timeoutId = null;
      }
    }, 7000);
  };

  // Check to see if the notification is already showing
  if (element.classList.contains("show")) {
    element.classList.remove("show");
    setTimeout(() => {
      manageNotification();
    }, 500);
  } else {
    manageNotification();
  }
};

export const ErrorMessage = showNotification("light-red", "red");
export const GeneralMessage = showNotification("purple-light", "pt-purple");
export const SuccessMessage = showNotification("light-mint", "dark-mint");
